import React, { useState, useEffect } from 'react';
import { projectsApi } from '../../utils/api';
import { ProjectCreateForm } from '../marketplace/ProjectCreateForm';
import { getAISettings } from '../../utils/aiSettings';

interface Project {
  _id: string;
  title: string;
  description?: string;
  status: 'draft' | 'listed' | 'completed';
  itemCount?: number;
  createdAt: string;
}

interface PhotoAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (assignment: PhotoAssignment) => void;
}

export interface PhotoAssignment {
  projectId?: string;
  projectTitle?: string;
  createItems: boolean;
  processLater: boolean;
}

export const PhotoAssignmentModal: React.FC<PhotoAssignmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [createItems, setCreateItems] = useState(true);
  const [showCreateProject, setShowCreateProject] = useState(false);
  
  // Get AI settings to determine if multiple items per photo is enabled
  const aiSettings = getAISettings();
  const multipleItemsEnabled = aiSettings.multipleItemsPerPhoto;

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      console.log('PhotoAssignmentModal: Loading projects...');
      const response = await projectsApi.list({ mine: true });
      console.log('PhotoAssignmentModal: Projects response:', response.data);
      if (response.data.success) {
        const projectsList = response.data.data || [];
        console.log('PhotoAssignmentModal: Loaded projects:', projectsList.length, projectsList);
        setProjects(projectsList);
      } else {
        console.warn('PhotoAssignmentModal: Failed to load projects:', response.data);
      }
    } catch (error) {
      console.error('PhotoAssignmentModal: Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = (project: any) => {
    // Add the new project to the list
    setProjects(prev => [project, ...prev]);
    // Select the newly created project
    setSelectedProjectId(project._id);
    // Close the project creation form
    setShowCreateProject(false);
  };

  const handleConfirm = () => {
    const selectedProject = projects.find(p => p._id === selectedProjectId);
    
    onConfirm({
      projectId: selectedProjectId || undefined,
      projectTitle: selectedProject?.title,
      createItems,
      processLater: !createItems
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Photo Assignment
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Choose how you'd like to handle this photo:
            </div>

            {/* Project Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Assign to Project (Optional)
              </label>
              
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.length === 0 ? (
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
                      <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                        No projects yet. Create one below or continue without a project.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg 
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                               focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">No Project (Standalone Photo)</option>
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {projects.length === 0 
                      ? 'Projects help organize photos from the same job or location'
                      : `${projects.length} project${projects.length !== 1 ? 's' : ''} available`
                    }
                  </p>
                  
                  {/* Create New Project Button */}
                  <button
                    onClick={() => setShowCreateProject(true)}
                    className="w-full flex items-center justify-center space-x-2 px-3 py-2 
                             border border-dashed border-slate-300 dark:border-slate-600 rounded-lg
                             text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100
                             hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-sm">Create New Project</span>
                  </button>
                </div>
              )}
            </div>

            {/* Processing Options */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Processing Options
              </label>
              
              {/* Full-width toggle */}
              <div className="relative">
                <button
                  onClick={() => setCreateItems(!createItems)}
                  className={`
                    w-full p-4 rounded-lg border-2 transition-all duration-200
                    ${createItems 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {createItems ? (multipleItemsEnabled ? 'Auto-Create Items from Detections' : 'Auto-Create Item from Photo') : 'Just Save Photo'}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {createItems 
                          ? (multipleItemsEnabled 
                              ? 'Automatically create an item for each detected object with AI analysis'
                              : 'Automatically create a single item from the photo with AI analysis')
                          : 'Save photo only, create items manually later from the Photos page'
                        }
                      </div>
                    </div>
                    <div className={`
                      flex-shrink-0 ml-4 w-12 h-6 rounded-full transition-colors duration-200
                      ${createItems ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'}
                    `}>
                      <div className={`
                        w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5
                        ${createItems ? 'translate-x-6' : 'translate-x-0.5'}
                      `} />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onClose}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="btn-primary"
            >
              Start
            </button>
          </div>
        </div>
      </div>

      {/* Project Create Modal */}
      {showCreateProject && (
        <ProjectCreateForm
          onSuccess={handleProjectCreated}
          onCancel={() => setShowCreateProject(false)}
        />
      )}
    </>
  );
};
